"use client"

import type {
  FooterNavigationBadge,
  FooterNavigationItemWithMarket,
} from "./types"
import { useState } from "react"
import Image from "next/image"
import { usePathname } from "next/navigation"
import { Link } from "@local/ui/src/components/ui/Link"
import { Modal } from "@local/ui/src/components/ui/Modal"
import dynamic from "next/dynamic"
import { CookiePreferencesButton } from "./CookiePreferencesButton"
import { getT } from "@local/i18n/src/t"
import {
  getLanguageForMarket,
  localizeMarketPath,
  marketBaseOf,
} from "@local/i18n/src/languages"
import { footer } from "@local/i18n/src/catalogs/footer"
import { a11y } from "@local/i18n/src/catalogs/a11y"

const BadgeImage = ({ badge }: { badge: FooterNavigationBadge }) => (
  <Image
    src={badge.src}
    alt={badge.alt}
    width={badge.width}
    height={badge.height}
    aria-hidden="true"
    className="h-4 w-auto"
  />
)

// Dynamically import static page components for modal display
const AboutUs = dynamic(
  () => import("../StaticPages/AboutUs").then((mod) => mod.AboutUs),
  { ssr: false }
)

const VeteransCharities = dynamic(
  () =>
    import("../StaticPages/VeteransCharities").then(
      (mod) => mod.VeteransCharities
    ),
  { ssr: false }
)

const BaerskinStories = dynamic(
  () =>
    import("../StaticPages/BaerskinStories").then((mod) => mod.BaerskinStories),
  { ssr: false }
)

const PrivacyPolicy = dynamic(
  () => import("../StaticPages/PrivacyPolicy").then((mod) => mod.PrivacyPolicy),
  { ssr: false }
)

const TermsConditions = dynamic(
  () =>
    import("../StaticPages/TermsConditions").then((mod) => mod.TermsConditions),
  { ssr: false }
)

const ShippingReturnsRefunds = dynamic(
  () =>
    import("../StaticPages/ShippingReturnsRefunds").then(
      (mod) => mod.ShippingReturnsRefunds
    ),
  { ssr: false }
)

const SecurePayments = dynamic(
  () =>
    import("../StaticPages/SecurePayments").then((mod) => mod.SecurePayments),
  { ssr: false }
)

const Accessibility = dynamic(
  () => import("../StaticPages/Accessibility").then((mod) => mod.Accessibility),
  { ssr: false }
)

/**
 * Determines if a URL should open in a modal instead of navigating
 */
const shouldOpenModal = (url: string) => {
  return (
    url.includes("about-us") ||
    url.includes("our-story") ||
    url.includes("privacy-policy") ||
    url.includes("terms-and-conditions") ||
    url.includes("shipping-returns-and-refunds") ||
    url.includes("secure-payments") ||
    url.includes("veterans-charities") ||
    url.includes("baerskin-stories") ||
    url.includes("accessibility")
  )
}

/**
 * Gets the appropriate modal content based on URL
 */
const getModalContent = (market: string, url?: string) => {
  if (!url) return null

  switch (true) {
    case url.includes("about-us") || url.includes("our-story"):
      return <AboutUs market={market} />
    case url.includes("privacy-policy"):
      return <PrivacyPolicy market={market} />
    case url.includes("terms-and-conditions"):
      return <TermsConditions market={market} />
    case url.includes("shipping-returns-and-refunds"):
      return <ShippingReturnsRefunds market={market} />
    case url.includes("secure-payments"):
      return <SecurePayments market={market} />
    case url.includes("veterans-charities"):
      return <VeteransCharities market={market} />
    case url.includes("baerskin-stories"):
      return <BaerskinStories market={market} />
    case url.includes("accessibility"):
      return <Accessibility market={market} />
    default:
      return null
  }
}

interface FooterLinkItemProps {
  name: string
  url: string
  market: string
  badge?: FooterNavigationBadge
}

const FooterLinkItem = ({ name, url, market, badge }: FooterLinkItemProps) => {
  const t = getT(getLanguageForMarket(market), { a11y })
  const [modalOpen, setModalOpen] = useState(false)
  const pathname = usePathname()
  const [lastPathname, setLastPathname] = useState(pathname)

  // Close modal on route change so inline links inside the modal navigate cleanly
  if (lastPathname !== pathname) {
    setLastPathname(pathname)
    if (modalOpen) setModalOpen(false)
  }

  // Check if this is an external link or should open in modal
  const isExternal = /^(https?|mailto|tel|ftp):/i.test(url)
  const openInModal = !isExternal && shouldOpenModal(url)
  const modalContent = getModalContent(market, url)

  if (isExternal) {
    return (
      <a
        href={url}
        target="_blank"
        rel={/^(mailto|tel):/i.test(url) ? undefined : "noopener noreferrer"}
        aria-label={t("a11y.openInNewTab", { name })}
        className="inline-flex items-center gap-2 text-base"
      >
        {badge && <BadgeImage badge={badge} />}
        <span>{name}</span>
      </a>
    )
  }

  if (openInModal) {
    return (
      <>
        <button
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center gap-2 text-left text-base hover:text-gray-300"
          type="button"
          aria-label={t("a11y.openInModal", { name })}
        >
          {badge && <BadgeImage badge={badge} />}
          <span>{name}</span>
        </button>
        <Modal
          open={modalOpen}
          toggle={setModalOpen}
          contentClassName="max-w-4xl"
        >
          {modalContent}
        </Modal>
      </>
    )
  }

  return (
    <Link
      href={localizeMarketPath(
        url.replace("%%MARKET%%", marketBaseOf(market)),
        market
      )}
      prefetch={false}
      className="inline-flex items-center gap-2 text-base"
    >
      {badge && <BadgeImage badge={badge} />}
      <span>{name}</span>
    </Link>
  )
}

export const FooterListItemWithModal = ({
  id,
  market,
  name,
  i18nKey,
  subitems,
  external,
}: FooterNavigationItemWithMarket) => {
  const t = getT(getLanguageForMarket(market), { footer, a11y })
  const visibleSubitems = subitems?.filter(
    ({ markets }) => !markets || markets.includes(marketBaseOf(market))
  )

  return (
    <div className="text-left text-white">
      <h5 className="leading-heading mb-2 text-lg font-semibold lg:mb-3">
        {i18nKey ? t(i18nKey as Parameters<typeof t>[0]) : name}
      </h5>
      <ul className="flex flex-col gap-3">
        {visibleSubitems?.map(({ name, i18nKey, url, action, badge }, i) => {
          const label = i18nKey ? t(i18nKey as Parameters<typeof t>[0]) : name
          return (
            <li
              key={`footer-list-subitem-${id}-${i}`}
              className="my-0! whitespace-nowrap"
            >
              {action === "cookie-preferences" ? (
                <CookiePreferencesButton name={label} market={market} />
              ) : /^(https?|mailto|tel|ftp):/i.test(url) || external ? (
                <a
                  href={url}
                  target="_blank"
                  rel={
                    /^(mailto|tel):/i.test(url)
                      ? undefined
                      : "noopener noreferrer"
                  }
                  aria-label={t("a11y.openInNewTab", { name: label })}
                  className="inline-flex items-center gap-2 text-base"
                >
                  {badge && <BadgeImage badge={badge} />}
                  <span>{label}</span>
                </a>
              ) : (
                <FooterLinkItem
                  name={label}
                  url={url}
                  market={market}
                  badge={badge}
                />
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
